import type { ApiCompanyProfessionals } from '@/types/api';
import { usePhoneAuthFlow } from '@/hooks/usePhoneAuthFlow';
import type { BookingSelectedTime, BookingStep } from '@/types/booking-flow';
import type { Service } from '@/types/domain/service';
import { useCallback, useMemo, useState } from 'react';

const BASE_STEP_ORDER: BookingStep[] = ['services', 'professional', 'datetime', 'summary', 'auth', 'confirmed'];
const ENTRY_MODE_STEP_ORDER: BookingStep[] = [
  'services',
  'entryMode',
  'professional',
  'datetime',
  'summary',
  'auth',
  'confirmed',
];
const BASE_PROGRESS_STEPS: BookingStep[] = ['services', 'professional', 'datetime', 'summary'];
const ENTRY_MODE_PROGRESS_STEPS: BookingStep[] = ['services', 'entryMode', 'professional', 'datetime', 'summary'];
const STEP_LABELS: Record<BookingStep, string> = {
  services: 'Serviços',
  entryMode: 'Atendimento',
  professional: 'Profissional',
  datetime: 'Data e horário',
  summary: 'Resumo',
  auth: 'Seus dados',
  confirmed: 'Confirmado',
};

interface UseBookingSelectionFlowParams {
  allowMultipleServicesPerBooking: boolean;
  includeEntryModeStep?: boolean;
  initialSelectedServices: string[];
  professionals: ApiCompanyProfessionals[];
  services: Service[];
}

export function useBookingSelectionFlow({
  allowMultipleServicesPerBooking,
  includeEntryModeStep = false,
  initialSelectedServices,
  professionals,
  services,
}: UseBookingSelectionFlowParams) {
  const stepOrder = includeEntryModeStep ? ENTRY_MODE_STEP_ORDER : BASE_STEP_ORDER;
  const progressSteps = includeEntryModeStep ? ENTRY_MODE_PROGRESS_STEPS : BASE_PROGRESS_STEPS;
  const initialStep =
    initialSelectedServices.length > 0 ? (includeEntryModeStep ? 'entryMode' : 'professional') : 'services';
  const [step, setStep] = useState<BookingStep>(initialStep);
  const [selectedServicesState, setSelectedServicesState] = useState<string[]>(initialSelectedServices);
  const [selectedPro, setSelectedPro] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<BookingSelectedTime | null>(null);
  const phoneAuthFlow = usePhoneAuthFlow();
  const selectedServices = allowMultipleServicesPerBooking ? selectedServicesState : selectedServicesState.slice(0, 1);

  const setSelectedServices = (nextServices: string[] | ((previousServices: string[]) => string[])) => {
    setSelectedServicesState((previousServices) => {
      const nextValue = typeof nextServices === 'function' ? nextServices(previousServices) : nextServices;

      return allowMultipleServicesPerBooking ? nextValue : nextValue.slice(0, 1);
    });
  };

  const selectedServiceItems = useMemo(
    () =>
      selectedServices.flatMap((serviceId) => {
        const service = services.find((currentService) => currentService.id === serviceId);

        return service ? [service] : [];
      }),
    [selectedServices, services],
  );

  const totalPrice = useMemo(
    () => selectedServiceItems.reduce((sum, service) => sum + (service.price || 0), 0),
    [selectedServiceItems],
  );

  const totalDuration = useMemo(
    () =>
      selectedServiceItems
        .flatMap((service) => {
          if (!service.durationMin) {
            return [];
          }

          return [`${service.durationMin} min`];
        })
        .join(' + '),
    [selectedServiceItems],
  );

  const professionalId = selectedPro === '' ? undefined : selectedPro;
  const proName =
    selectedPro === 'any' ? 'Qualquer profissional' : professionals.find((pro) => pro.id === selectedPro)?.name || '';
  const currentStepIndex = stepOrder.indexOf(step);
  const activeProgressIndex = progressSteps.indexOf(step);

  const canContinue = useMemo(() => {
    switch (step) {
      case 'services':
        return selectedServices.length > 0;
      case 'entryMode':
        return false;
      case 'professional':
        return selectedPro !== '';
      case 'datetime':
        return selectedTime !== null;
      case 'summary':
        return true;
      case 'auth':
        return phoneAuthFlow.authStep === 'name' && phoneAuthFlow.userName.trim().length > 0;
      default:
        return false;
    }
  }, [phoneAuthFlow.authStep, phoneAuthFlow.userName, selectedPro, selectedServices.length, selectedTime, step]);

  const toggleService = (serviceId: string) => {
    setSelectedServices((previousServices) => {
      if (previousServices.includes(serviceId)) {
        return previousServices.filter((currentServiceId) => currentServiceId !== serviceId);
      }

      if (!allowMultipleServicesPerBooking && previousServices.length > 0) {
        return previousServices;
      }

      return [...previousServices, serviceId];
    });
  };

  const goNextStep = () => {
    setStep((currentStep) => {
      const nextStepIndex = stepOrder.indexOf(currentStep) + 1;

      return nextStepIndex < stepOrder.length ? stepOrder[nextStepIndex] : currentStep;
    });
  };

  const goPrevStep = () => {
    setStep((currentStep) => {
      const previousStepIndex = stepOrder.indexOf(currentStep) - 1;

      return previousStepIndex >= 0 ? stepOrder[previousStepIndex] : currentStep;
    });
  };

  const selectProfessional = (professionalSelection: string) => {
    setSelectedPro(professionalSelection);
  };

  const selectTime = (date: string, time: string, label: string) => {
    setSelectedTime({ date, time, label });
  };

  const clearSelectedTime = useCallback(() => {
    setSelectedTime(null);
  }, []);

  return {
    activeProgressIndex,
    authStep: phoneAuthFlow.authStep,
    canContinue,
    clearSelectedTime,
    currentStepIndex,
    goNextStep,
    goPrevStep,
    phoneNumber: phoneAuthFlow.phoneNumber,
    proName,
    professionalId,
    progressSteps,
    selectProfessional,
    selectedPro,
    selectedServiceItems,
    selectedServices,
    selectedTime,
    selectTime,
    setAuthStep: phoneAuthFlow.setAuthStep,
    setPhoneNumber: phoneAuthFlow.setPhoneNumber,
    setSelectedServices,
    setStep,
    setUserName: phoneAuthFlow.setUserName,
    setVerificationCode: phoneAuthFlow.setVerificationCode,
    step,
    stepLabels: STEP_LABELS,
    totalDuration,
    totalPrice,
    toggleService,
    userName: phoneAuthFlow.userName,
    verificationCode: phoneAuthFlow.verificationCode,
  };
}
